import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Event, NavigationEnd, NavigationError, NavigationStart, Router, RouterLink } from '@angular/router';
import {
  faBars,
  faBell,
  faCircleUser,
  faFolder,
  faPlaneDeparture,
  faPowerOff,
  faUser,
  IconDefinition
} from '@fortawesome/free-solid-svg-icons';
import { Subscription } from 'rxjs';
import { INotification } from '@simplyfly/shared';
import {
  getNotificationIconKey,
  getNotificationUrl,
  NotificationIconKey
} from 'src/app/interfaces/notification.helpers';
import { EnumAcl } from '@simplyfly/shared';
import { IUser } from '@simplyfly/shared';
import { getUserFullname } from 'src/app/interfaces/user.helpers';
import { AclService } from 'src/app/services/acl.service';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { NotificationService } from 'src/app/services/notifications/notifications.service';
import { AppDataService } from 'src/app/services/data/app-data.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { HeaderMenuBreadcrumbsComponent } from '../header-menu-breadcrumbs/header-menu-breadcrumbs.component';
import { HeaderMenuCurrentUrlLogsComponent } from '../header-menu-current-url-logs/header-menu-current-url-logs.component';
import { DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

const notificationIcons: Record<NotificationIconKey, IconDefinition> = {
  bell: faBell,
  folder: faFolder,
  planeDeparture: faPlaneDeparture
};

interface INotificationListItem extends INotification {
  icon: IconDefinition;
}

@Component({
    selector: 'app-header-menu',
    templateUrl: './header-menu.component.html',
    styleUrls: ['./header-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FaIconComponent, HeaderMenuBreadcrumbsComponent, HeaderMenuCurrentUrlLogsComponent, RouterLink, DatePipe, TranslateModule]
})
export class HeaderMenuComponent implements OnInit, OnDestroy {
  EnumAcl = EnumAcl;

  faUser = faUser;
  faPowerOff = faPowerOff;
  faBars = faBars;
  faCircleUser = faCircleUser;
  faBell = faBell;

  getUserFullname = getUserFullname;

  displayBreadcrumbs = false;

  currentUser: IUser | null = null;

  currentRoute = '/';

  displayShopSelect = false;

  loadingNotifications = false;

  currentNotificationPagination = 'user-notifications-list';
  notifications: INotificationListItem[] = [];
  unreadCount = 0;
  firstLoadNotifications = false;

  private subscriptions = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private appDataService: AppDataService,
    private loaderService: LoaderService,
    private notificationService: NotificationService,
    private aclService: AclService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.authService.userObservable.subscribe((user: IUser) => {
        this.currentUser = user;
        this.changeDetectorRef.markForCheck();

        if (this.currentUser && !this.firstLoadNotifications) {
          this.firstLoadNotifications = true;

          this.loadNotifications();
          this.loadUnreadNotifications();
        }
      })
    );

    this.currentRoute = `${window.location.pathname || '/'}${window.location.search || ''}${
      window.location.hash || ''
    }`;
    this.updatedCurrentUrl();

    this.subscriptions.add(
      this.router.events.subscribe((event: Event) => {
        if (event instanceof NavigationStart) {
          // Show progress spinner or progress bar
          // console.log('Route change detected');
        }

        if (event instanceof NavigationEnd) {
          // Hide progress spinner or progress bar
          this.currentRoute = event.urlAfterRedirects || event.url;
          this.changeDetectorRef.markForCheck();
        }

        if (event instanceof NavigationError) {
          // Hide progress spinner or progress bar

          // Present error to user
          console.log(event.error);
        }

        this.updatedCurrentUrl();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  updatedCurrentUrl(): void {
    this.displayBreadcrumbs = false;
    this.displayShopSelect = false;

    if (this.currentRoute.match('/admin')) {
      this.displayBreadcrumbs = true;
    }

    this.changeDetectorRef.markForCheck();
  }

  async signOut(): Promise<void> {
    this.loaderService.presentLoader();

    try {
      // await this.usersService.signOut();
      await this.authService.signOutCurrentUser();

      this.loaderService.hideLoaderOnSuccess();

      this.router.navigateByUrl('/');
    } catch (err: any) {
      this.loaderService.hideLoaderOnFailure(err.message);
    }
  }

  loadUnreadNotifications(): void {
    if (this.currentUser) {
      this.subscriptions.add(
        this.notificationService
          .getAllForUserUnread(this.currentUser.id)
          .subscribe((notifications: INotification[]) => {
            this.unreadCount = notifications.length;
            this.changeDetectorRef.markForCheck();
          })
      );
    }
  }

  loadNotifications(): void {
    if (this.currentUser) {
      this.loadingNotifications = true;
      this.changeDetectorRef.markForCheck();

      this.subscriptions.add(
        this.notificationService
          .getLastXForUser(this.currentUser.id, 20)
          .subscribe((notifications: INotification[]) => {
            this.notifications = notifications.map(notification => ({
              ...notification,
              icon: notificationIcons[getNotificationIconKey(notification)]
            }));

            this.loadingNotifications = false;
            this.changeDetectorRef.markForCheck();
          })
      );
    }
  }

  async openNotification(notification: INotification): Promise<void> {
    this.notificationService.setAsRead(notification.id);

    const url: string | null = getNotificationUrl(notification);

    if (url) {
      this.router.navigateByUrl(url);
    }
  }

  hasAclAccess(id: EnumAcl): boolean {
    return this.aclService.hasAclAccess(id);
  }
}
